import { useEffect, useRef } from 'react';

interface LiveDemoSectionProps {
  onCTAClick: () => void;
}

export default function LiveDemoSection({ onCTAClick }: LiveDemoSectionProps) {
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add('opacity-100', 'translate-y-0');
          el.classList.remove('opacity-0', 'translate-y-8');
        }
      },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="py-20 md:py-28 bg-white relative overflow-hidden">
      {/* 極淡背景裝飾 */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 left-1/4 w-[500px] h-[300px] bg-[#00D4FF]/4 rounded-full blur-[120px]"></div>
        <div className="absolute bottom-0 right-1/4 w-[400px] h-[250px] bg-[#D4AF37]/5 rounded-full blur-[100px]"></div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 relative z-10">
        {/* 頂部標籤 */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-[#0A192F]/8 border border-[#0A192F]/15 text-[#0A192F] px-5 py-2 rounded-full text-xs sm:text-sm font-semibold uppercase tracking-widest mb-6">
            <i className="ri-computer-line text-[#00D4FF]"></i>
            <span>Live System Demo</span>
          </div>

          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#0A192F] leading-tight mb-4">
            您的專屬
            <span className="bg-gradient-to-r from-[#00D4FF] to-[#D4AF37] bg-clip-text text-transparent"> O2O 動態展間</span>
          </h2>

          <p className="text-gray-500 text-sm sm:text-base max-w-2xl mx-auto leading-relaxed">
            告別傳統拼裝網頁。我們採用業界頂級的「雙核架構」：由專屬形象官網負責品牌引流與 SEO 曝光；當客戶準備看商品時，一鍵全螢幕切換至獨立的 Web App 動態展間。徹底解決傳統網頁塞滿商品導致的卡頓，提供宛如原生 App 般極致流暢的瀏覽體驗。
          </p>
        </div>

        {/* 主視覺 Mockup 卡片 */}
        <div
          ref={cardRef}
          className="opacity-0 translate-y-8 transition-all duration-700 ease-out"
        >
          {/* MacBook 外框 */}
          <div className="relative mx-auto max-w-4xl">
            {/* 螢幕外框 */}
            <div className="bg-[#1A1A1A] rounded-2xl p-3 shadow-[0_30px_80px_rgba(0,0,0,0.18),0_0_0_1px_rgba(0,0,0,0.08)]">
              {/* 頂部攝影機點 */}
              <div className="flex justify-center mb-2">
                <div className="w-2 h-2 bg-[#3A3A3A] rounded-full"></div>
              </div>
              {/* 螢幕內容區 */}
              <div className="rounded-lg overflow-hidden bg-[#0A192F] relative" style={{ aspectRatio: '16/9' }}>
                <img
                  src="https://public.readdy.ai/ai/img_res/4b008fd952577ad7f972e01dcee2e64b.jpg"
                  alt="O2O 動態展間系統截圖"
                  className="w-full h-full object-cover object-top"
                />
                {/* 螢幕輕微光暈疊層 */}
                <div className="absolute inset-0 bg-gradient-to-tr from-[#00D4FF]/5 via-transparent to-[#D4AF37]/5 pointer-events-none"></div>
                {/* 佔位說明文字覆蓋 */}
                <div className="absolute inset-0 flex items-center justify-center bg-[#0A192F]/60 backdrop-blur-[1px]">
                  <div className="text-center px-6">
                    <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-[#D4AF37]/15 border border-[#D4AF37]/30 rounded-2xl">
                      <i className="ri-computer-line text-3xl text-[#D4AF37]"></i>
                    </div>
                    <p className="text-white/60 text-sm font-light tracking-wider mb-1">系統截圖即將上線</p>
                    <p className="text-white/40 text-xs">您可在後台替換為實際系統畫面截圖</p>
                  </div>
                </div>
              </div>
              {/* 底部底座 */}
              <div className="mt-2 flex justify-center">
                <div className="w-24 h-1.5 bg-[#2A2A2A] rounded-full"></div>
              </div>
            </div>
            {/* 底座腳 */}
            <div className="flex justify-center">
              <div className="w-40 h-3 bg-[#1A1A1A] rounded-b-xl shadow-[0_4px_16px_rgba(0,0,0,0.12)]"></div>
            </div>

            {/* 浮動狀態徽章 */}
            <div className="absolute -left-4 top-1/3 bg-white border border-[#D4AF37]/30 rounded-xl p-3.5 shadow-lg hidden md:block">
              <div className="flex items-center gap-2.5">
                <div className="w-2.5 h-2.5 bg-green-400 rounded-full animate-pulse flex-shrink-0"></div>
                <div>
                  <div className="text-[10px] text-gray-400 leading-tight">系統狀態</div>
                  <div className="text-xs font-bold text-[#0A192F]">全天候運作中</div>
                </div>
              </div>
            </div>

            <div className="absolute -right-4 top-1/2 bg-white border border-[#00D4FF]/30 rounded-xl p-3.5 shadow-lg hidden md:block">
              <div className="text-[10px] text-gray-400 mb-0.5">名單轉換率</div>
              <div className="text-base font-bold text-[#00D4FF]">+38%</div>
            </div>
          </div>

          {/* CTA 按鈕區域 */}
          <div className="mt-10 md:mt-12">
            {/* 主要 Live Demo 按鈕 */}
            <div className="text-center mb-8">
              <a
                href="https://app.fullchainmarketing.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 bg-[#0A192F] text-white px-8 md:px-12 py-4 md:py-5 rounded-full text-sm md:text-base font-bold shadow-[0_0_30px_rgba(10,25,47,0.2),0_4px_20px_rgba(0,212,255,0.15)] hover:shadow-[0_0_50px_rgba(0,212,255,0.3),0_8px_30px_rgba(0,212,255,0.2)] hover:bg-[#0F2744] transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-fullscreen-line text-[#00D4FF] text-lg"></i>
                全螢幕啟動您的專屬系統 (Live Demo)
                <i className="ri-external-link-line text-[#D4AF37] text-base"></i>
              </a>
              <p className="text-gray-400 text-xs sm:text-sm mt-4 font-light tracking-wide">
                為確保最佳體驗，系統將於獨立安全通道全螢幕啟動
              </p>
            </div>

            {/* 分隔線 + 諮詢 CTA 橫幅 */}
            <div className="relative border border-[#0A192F]/12 rounded-2xl p-6 sm:p-8 bg-gradient-to-r from-[#0A192F]/4 via-[#00D4FF]/3 to-[#D4AF37]/4 overflow-hidden">
              {/* 左側裝飾線 */}
              <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-[#00D4FF] to-[#D4AF37] rounded-l-2xl"></div>
              <div className="flex flex-col sm:flex-row items-center justify-between gap-5 pl-4">
                <div className="text-center sm:text-left">
                  <p className="text-[#0A192F] font-bold text-base sm:text-lg mb-1">
                    看完 Demo，想為自己的企業打造一套？
                  </p>
                  <p className="text-gray-500 text-xs sm:text-sm">
                    我們的顧問將為您免費規劃專屬的 O2O 系統架構與獲客漏斗方案
                  </p>
                </div>
                <button
                  onClick={onCTAClick}
                  className="flex-shrink-0 inline-flex items-center gap-2 bg-gradient-to-r from-[#0A192F] to-[#0F2744] text-white px-7 py-3.5 rounded-full text-sm font-bold hover:from-[#0F2744] hover:to-[#1a3a6a] transition-all duration-300 shadow-md hover:shadow-lg whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-calendar-check-line text-[#00D4FF]"></i>
                  免費預約系統規劃諮詢
                  <i className="ri-arrow-right-line text-[#D4AF37]"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
